import {
  Button,
  Table,
  Form,
  Row,
  Col,
  Badge,
  Spinner,
  Alert,
  Modal,
  Card,
} from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import PageFade from "../../components/animation/PageFade";
import {
  useGetCategoriesQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} from "../../services/api";
import { toast } from "react-toastify";
import { Category } from "../../types";

// Optimized Category Image Component with lazy loading and placeholder
const CategoryImage = ({ src, alt }: { src: string; alt: string }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Check if image is already loaded (cached images)
    if (imgRef.current?.complete) {
      setIsLoading(false);
    }
  }, []);

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError) {
    return (
      <div
        style={{
          width: "50px",
          height: "50px",
          backgroundColor: "#f0f0f0",
          borderRadius: "4px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "10px",
          color: "#999",
        }}
      >
        No Image
      </div>
    );
  }

  return (
    <div style={{ position: "relative", width: "50px", height: "50px" }}>
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "50px",
            height: "50px",
            backgroundColor: "#f0f0f0",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
          }}
        >
          <Spinner animation="border" size="sm" />
        </div>
      )}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        width={50}
        height={50}
        onLoad={handleLoad}
        onError={handleError}
        style={{
          width: "50px",
          height: "50px",
          objectFit: "cover",
          borderRadius: "4px",
          opacity: isLoading ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        }}
      />
    </div>
  );
};

export default function Categories() {
  const { data, isLoading, error, refetch } = useGetCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();
  const [deleteCategory, { isLoading: isDeleting }] =
    useDeleteCategoryMutation();

  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    image: null as File | null,
  });
  const [existingImage, setExistingImage] = useState<string | null>(null);

  const categories = data?.data || [];

  const handleOpenModal = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || "",
        description: category.description || "",
        image: null,
      });
      setExistingImage(category.image || null);
    } else {
      setEditingCategory(null);
      setFormData({ name: "", description: "", image: null });
      setExistingImage(null);
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({ name: "", description: "", image: null });
    setExistingImage(null);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData((prev) => ({ ...prev, image: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    const submitFormData = new FormData();
    submitFormData.append("name", formData.name);
    if (formData.description)
      submitFormData.append("description", formData.description);
    
    // According to guide: Use multipart/form-data for Categories
    // If a new image is selected, append it.
    if (formData.image) {
      submitFormData.append("image", formData.image);
    }

    try {
      if (editingCategory) {
        await updateCategory({
          id: editingCategory._id,
          formData: submitFormData,
        }).unwrap();
        toast.success("Category updated successfully");
      } else {
        await createCategory(submitFormData).unwrap();
        toast.success("Category created successfully");
      }
      handleCloseModal();
    } catch (error: any) {
      console.error('Category operation failed:', error);
      toast.error(
        error?.data?.message ||
          `Failed to ${editingCategory ? "update" : "create"} category`
      );
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      try {
        await deleteCategory(id).unwrap();
        toast.success("Category deleted successfully");
      } catch (error: any) {
        toast.error(error?.data?.message || "Failed to delete category");
      }
    }
  };

  if (isLoading) {
    return (
      <PageFade>
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </PageFade>
    );
  }

  if (error) {
    const errorMessage =
      (error as any)?.data?.message ||
      (error as any)?.error ||
      "Failed to load categories. Please check your connection and try again.";
    const status = (error as any)?.status;

    return (
      <PageFade>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Categories</Alert.Heading>
          <p>{errorMessage}</p>
          {status === 401 && (
            <p className="mb-0">
              <small>Your session may have expired. Please log in again.</small>
            </p>
          )}
          {status === 403 && (
            <p className="mb-0">
              <small>You don't have permission to access this resource.</small>
            </p>
          )}
          <hr />
          <div className="d-flex justify-content-end">
            <Button
              onClick={() => refetch()}
              variant="outline-danger"
              size="sm"
            >
              Retry
            </Button>
          </div>
        </Alert>
      </PageFade>
    );
  }

  return (
    <PageFade>
      <Row className="mb-3">
        <Col>
          <h3>Categories</h3>
        </Col>
        <Col className="text-end">
          <Button onClick={() => handleOpenModal()}>Add Category</Button>
        </Col>
      </Row>
      {categories.length === 0 ? (
        <Alert variant="info">
          No categories found. Click "Add Category" to create your first
          category.
        </Alert>
      ) : (
        <Table hover responsive>
          <thead>
            <tr>
              <th>Image</th>
              <th>Name</th>
              <th>Description</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category) => (
              <tr key={category._id} style={{ backgroundColor: "#fff" }}>
                <td>
                  {category.image ? (
                    <CategoryImage src={category.image} alt={category.name} />
                  ) : (
                    <div
                      style={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "10px",
                        color: "#999",
                      }}
                    >
                      No Image
                    </div>
                  )}
                </td>
                <td>{category.name}</td>
                <td>{category.description || "-"}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="me-2"
                    onClick={() => handleOpenModal(category)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleDelete(category._id, category.name)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingCategory ? "Edit Category" : "Add Category"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Category name"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Category description"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Image</Form.Label>
                  {existingImage && (
                    <div className="mb-2">
                      <img
                        src={existingImage}
                        alt="Current"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                  )}
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                  <Form.Text className="text-muted">
                    Leave empty to keep existing image
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button type="submit" disabled={isCreating || isUpdating}>
              {isCreating || isUpdating ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingCategory ? "Updating..." : "Creating..."}
                </>
              ) : editingCategory ? (
                "Update Category"
              ) : (
                "Create Category"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageFade>
  );
}
